import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Calendar, CheckCircle2, ChevronDown, Info } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import useEmblaCarousel from "embla-carousel-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { fetchAPI } from "@/lib/api";

interface TourPlan {
  id: number;
  slug: string;
  title: string;
  subtitle: string;
  description: string;
  duration_nights: number;
  duration_days: number;
  base_price: string;
  plan_type: string;
  featured_image_url: string;
  is_active: number;
  highlights?: { badge_text: string; icon_name: string }[];
  sightseeing?: { title: string; image_url: string; sort_order: number }[];
  features?: { feature_category: string; feature_text: string }[];
  terms?: { tab_name: string; content: string }[];
}

const SITE_DATA = {
  aboutImage: "https://images.unsplash.com/photo-1596783046522-bb17551cc369?auto=format&fit=crop&q=80",
  sightseeing: [
    { title: "Key Attraction 1", img: "https://images.unsplash.com/photo-1590518770428-1b6cc0ca0495?auto=format&fit=crop&q=80" },
    { title: "Key Attraction 2", img: "https://images.unsplash.com/photo-1574349635099-07bc1495c207?auto=format&fit=crop&q=80" },
    { title: "Key Attraction 3", img: "https://images.unsplash.com/photo-1605649487212-4d4b14d35c59?auto=format&fit=crop&q=80" }
  ],
  accommodation: [
    "https://images.unsplash.com/photo-1566665797739-1674de7a421a?auto=format&fit=crop&q=80",
    "https://images.unsplash.com/photo-1582719508461-905c673771fd?auto=format&fit=crop&q=80",
    "https://images.unsplash.com/photo-1590490359854-dfba196a56cd?auto=format&fit=crop&q=80"
  ]
};

export default function TourDetail() {
  const { slug } = useParams();
  const [plan, setPlan] = useState<TourPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  const [emblaRef] = useEmblaCarousel({ loop: true, align: "center" });

  useEffect(() => {
    if (slug) {
      fetchAPI(`?type=tour_plans&slug=${slug}`)
        .then((data: any) => {
          if (data && data.id) {
            setPlan(data);
          } else {
            setError("Tour not found");
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching plan:", err);
          setError("Failed to load tour plan");
          setLoading(false);
        });
    }
  }, [slug]);

  if (loading) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-background">
            <div className="animate-pulse flex flex-col items-center gap-4">
                <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin" />
                <p className="text-muted-foreground font-serif">Loading Tour Details...</p>
            </div>
        </div>
    );
  }

  if (error || !plan) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-background text-foreground">
        <h1 className="text-3xl font-serif text-destructive">Tour Not Found</h1>
        <p className="text-muted-foreground">We couldn't find the tour plan you're looking for.</p>
        <Link to="/tour-plans">
          <Button variant="outline" className="gap-2">
            <ArrowLeft className="w-4 h-4"/> Back to Tours
          </Button>
        </Link>
      </div>
    );
  }

  const bgImage = plan.featured_image_url || "https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?auto=format&fit=crop&q=80";

  return (
    <div className="min-h-screen bg-background font-sans selection:bg-orange-100 selection:text-orange-900 overflow-x-hidden">
      <SEO 
        title={plan.title} 
        description={plan.description || plan.subtitle}
      />

      <Navbar />

      {/* 1. HERO SECTION */}
      <section className="relative h-[80vh] min-h-[600px] w-full overflow-hidden flex flex-col justify-center">
        <motion.div 
            initial={{ scale: 1.1, opacity: 0.8 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="absolute inset-0 z-0"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-background/90 z-10" />
          <img 
            src={bgImage} 
            alt={plan.title} 
            className="w-full h-full object-cover"
          />
        </motion.div>

        <div className="container mx-auto px-6 relative z-20 flex flex-col items-center text-center mt-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <span className="inline-block py-1 px-4 rounded-full bg-primary/90 text-primary-foreground text-sm font-medium tracking-wide shadow-lg backdrop-blur-sm mb-6">
              Exclusive Journey
            </span>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl font-bold mb-6 tracking-tight text-white drop-shadow-lg">
              {plan.title}
            </h1>
            {plan.subtitle && (
              <p className="text-xl md:text-2xl text-sand max-w-3xl mx-auto mb-10 font-serif italic drop-shadow-md">
                "{plan.subtitle}"
              </p>
            )}

            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {(plan.highlights && plan.highlights.length > 0) ? plan.highlights.map((badge, i) => (
                <motion.div 
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 + 0.5 }}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-background/20 border border-white/20 backdrop-blur-md text-white shadow-lg"
                >
                  <CheckCircle2 className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">{badge.badge_text}</span>
                </motion.div>
              )) : [
                `${plan.duration_nights} Nights / ${plan.duration_days} Days`, 
                "Private Tour", 
                "Premium Stay"
              ].map((badge, i) => (
                <motion.div 
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 + 0.5 }}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-background/20 border border-white/20 backdrop-blur-md text-white shadow-lg"
                >
                  <CheckCircle2 className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">{badge}</span>
                </motion.div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <Button variant="sacred" size="lg" className="h-14 px-8 text-lg rounded-full shadow-xl">
                Book This Tour
              </Button>
            </div>
          </motion.div>
        </div>

        <motion.div 
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20"
        >
          <ChevronDown className="w-8 h-8 text-white/50" />
        </motion.div>
      </section>

      <div className="container mx-auto px-6 lg:px-8 -mt-16 relative z-30 pb-20">
          
        <div className="flex flex-wrap items-center justify-between mb-8">
            <Link to="/tour-plans" className="inline-flex items-center text-muted-foreground hover:text-primary transition-colors group">
                <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" /> 
                Back to all plans
            </Link>
        </div>

        <div className="bg-card rounded-3xl p-8 md:p-12 shadow-xl border border-border/50">
            {/* 2. ABOUT SECTION */}
            <div className="flex flex-col lg:flex-row items-center gap-16 mb-24">
                <motion.div 
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    className="lg:w-1/2 relative"
                >
                    <div className="absolute inset-0 bg-primary/10 rounded-[2.5rem] rotate-3 scale-105 -z-10 transition-transform hover:rotate-6" />
                    <img src={SITE_DATA.aboutImage} alt="About" className="rounded-3xl w-full h-[400px] object-cover shadow-lg border border-border" />
                </motion.div>
                <div className="lg:w-1/2">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6 text-foreground">The Experience</h2>
                    <div className="prose prose-lg text-muted-foreground whitespace-pre-wrap font-serif">
                        {plan.description || "Experience a thoughtfully curated journey through breathtaking landscapes. Enjoy premium accommodations, expert local guides, and seamless travel arrangements designed exclusively for your comfort."}
                    </div>
                </div>
            </div>

            <hr className="border-border my-16" />

            {/* 3. SIGHTSEEING SECTION */}
            {plan.sightseeing && plan.sightseeing.length > 0 && (
            <div className="mb-24">
                <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4 text-foreground">Highlights</h2>
                    <p className="text-muted-foreground text-lg font-serif italic">Uncover majestic beauty and curated destinations</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {plan.sightseeing.map((item, i) => (
                    <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.1 }}
                        whileHover={{ y: -5 }}
                        className="group relative h-64 rounded-2xl overflow-hidden shadow-soft border border-border"
                    >
                        <img src={item.image_url} alt={item.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                        <div className="absolute bottom-6 left-6 right-6">
                        <h3 className="text-xl font-bold font-serif text-white group-hover:text-primary transition-colors flex justify-between items-center">
                            {item.title}
                        </h3>
                        </div>
                    </motion.div>
                    ))}
                </div>
            </div>
            )}
            
            {plan.sightseeing && plan.sightseeing.length > 0 && <hr className="border-border my-16" />}

            {/* 4. PACKAGE DETAILS & 5. PRICING */}
            <div className="flex flex-col lg:flex-row gap-12">
                <div className="lg:w-7/12">
                    <h2 className="text-3xl font-serif font-bold mb-8 flex items-center gap-3 text-foreground">
                        <Info className="w-8 h-8 text-primary" />
                        Package Overview
                    </h2>
                    
                    <div className="space-y-6">
                        <div className="flex items-start gap-4 pb-6 border-b border-border">
                        <div className="p-3 rounded-xl bg-orange-100 dark:bg-orange-950/50 text-orange-600"><Calendar className="w-6 h-6" /></div>
                        <div>
                            <h4 className="text-xl font-serif font-semibold mb-1 text-foreground">Duration</h4>
                            <p className="text-muted-foreground text-lg">{plan.duration_nights} Nights / {plan.duration_days} Days</p>
                        </div>
                        </div>
                        
                        <div className="flex items-start gap-4 pb-6 border-b border-border">
                        <div className="p-3 rounded-xl bg-blue-100 dark:bg-blue-950/50 text-blue-600"><CheckCircle2 className="w-6 h-6" /></div>
                        <div>
                            <h4 className="text-xl font-serif font-semibold mb-1 text-foreground">Plan</h4>
                            <p className="text-muted-foreground text-lg mb-1">{plan.plan_type}</p>
                        </div>
                        </div>

                        <div className="flex items-start gap-4">
                        <div className="p-3 rounded-xl bg-green-100 dark:bg-green-950/50 text-green-600"><MapPin className="w-6 h-6" /></div>
                        <div>
                            <h4 className="text-xl font-serif font-semibold mb-1 text-foreground">Transport</h4>
                            <p className="text-muted-foreground text-lg">Private Car Included</p>
                        </div>
                        </div>
                    </div>
                </div>

                <div className="lg:w-5/12 flex flex-col justify-center">
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        className="rounded-3xl bg-sand border border-border p-8 md:p-10 text-center shadow-lg"
                    >
                        <h2 className="text-2xl font-serif font-bold text-muted-foreground mb-4 uppercase tracking-wider text-sm">Pricing (Starting from)</h2>
                        <div className="text-5xl font-bold text-foreground mb-2">
                            ₹{plan.base_price}
                        </div>
                        <p className="text-muted-foreground mb-8 italic font-serif">per person</p>

                        <div className="bg-background rounded-2xl p-6 text-left mb-8 border border-border shadow-sm">
                            <p className="font-semibold text-foreground mb-4 font-serif text-lg">Includes:</p>
                            <ul className="space-y-3">
                                {(plan.features && plan.features.length > 0) ? plan.features.map((item, i) => (
                                <li key={i} className="flex items-center gap-3 text-muted-foreground">
                                    <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
                                    {item.feature_text}
                                </li>
                                )) : ["Premium Accommodation", "Private Transport", "Welcome Drinks"].map((item, i) => (
                                <li key={i} className="flex items-center gap-3 text-muted-foreground">
                                    <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
                                    {item}
                                </li>
                                ))}
                            </ul>
                        </div>

                        <Button variant="sacred" className="w-full h-14 text-lg rounded-full shadow-md hover:shadow-lg transition-all">
                            Request This Package
                        </Button>
                    </motion.div>
                </div>
            </div>

            <hr className="border-border my-16" />

            {/* 6. ACCOMMODATION SECTION */}
            <div className="mb-24">
                <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4 text-foreground">The Properties</h2>
                    <p className="text-muted-foreground text-lg font-serif italic">Premium rooms with curated amenities designed for comfort and relaxation.</p>
                </div>

                <div className="overflow-hidden cursor-grab active:cursor-grabbing" ref={emblaRef}>
                    <div className="flex gap-6 relative">
                    {SITE_DATA.accommodation.map((img, i) => (
                        <div key={i} className="flex-[0_0_85%] md:flex-[0_0_60%] lg:flex-[0_0_45%] min-w-0 relative">
                        <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-border shadow-sm">
                            <img src={img} alt="Accommodation" className="w-full h-full object-cover select-none" />
                        </div>
                        </div>
                    ))}
                    </div>
                </div>
            </div>

            <hr className="border-border my-16" />

            {/* 7. TERMS & CONDITIONS */}
            <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl font-serif font-bold mb-8 text-center text-foreground">Important Information</h2>
                
                <Accordion type="single" collapsible className="w-full">
                    {(plan.terms && plan.terms.length > 0) ? plan.terms.map((term, i) => (
                        <AccordionItem key={i} value={`item-${i}`} className="border-border">
                            <AccordionTrigger className="text-lg font-serif text-foreground hover:text-primary">{term.tab_name}</AccordionTrigger>
                            <AccordionContent className="text-muted-foreground text-base leading-relaxed whitespace-pre-wrap">
                                {term.content}
                            </AccordionContent>
                        </AccordionItem>
                    )) : (
                        <>
                        <AccordionItem value="item-1" className="border-border">
                            <AccordionTrigger className="text-lg font-serif text-foreground hover:text-primary">Booking & Payments</AccordionTrigger>
                            <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                                A 50% advance is required to confirm the booking. Full payment must be completed at least 10 days prior to the travel date.
                            </AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="item-2" className="border-border">
                            <AccordionTrigger className="text-lg font-serif text-foreground hover:text-primary">Cancellation Policy</AccordionTrigger>
                            <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                                Cancellation charges apply based on the timeframe prior to departure. Please refer to our detailed cancellation policy document.
                            </AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="item-3" className="border-border">
                            <AccordionTrigger className="text-lg font-serif text-foreground hover:text-primary">Hotel Policy</AccordionTrigger>
                            <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                                Mandatory ID proof required during check-in. Standard check-in time is 2:00 PM and check-out is 11:00 AM.
                            </AccordionContent>
                        </AccordionItem>
                        </>
                    )}
                </Accordion>
            </div>
        </div>
      </div>

      {/* 8. CALL TO ACTION */}
      <section className="py-24 bg-sand border-t border-border">
        <div className="container mx-auto px-6 lg:px-8 text-center max-w-3xl">
          <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6 text-foreground">
            Ready for your escape?
          </h2>
          <p className="text-muted-foreground text-lg mb-10 font-serif italic">
            Connect with our travel experts to customize this itinerary.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-6">
            <Button variant="sacred" size="lg" className="h-14 px-10 text-lg rounded-full shadow-lg">
              Book Now
            </Button>
            <Button variant="outline" size="lg" className="h-14 px-10 text-lg rounded-full bg-background">
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
